import api from './api';
import { Customer } from './customers';
import { Business } from './businesses';
import { TaskType } from './taskTypes';
import { Task } from './tasks';
import { TaskTemplate } from './taskTemplates';
import { User } from './auth';

const unwrap = <T>(response: any): T => response.data?.data ?? response.data;

export interface DispatchCheckin {
  id?: number | string;
  dispatch_id: number;
  name: string;
  completed?: boolean;
  created_at: string;
  updated_at?: string;
  created_by?: number;
  updater?: User;
}

export interface DispatchCheckout {
  id?: number | string;
  dispatch_id: number;
  name: string;
  completed?: boolean;
  created_at: string;
  updated_at?: string;
  created_by?: number;
  updater?: User;
}

export interface DispatchCollect {
  id?: number | string;
  dispatch_id: number;
  name: string;
  attachment?: string | null;
  enabled: boolean;
  created_at: string;
  updated_at?: string;
  created_by?: number;
  updater?: User;
}

export interface DispatchReturn {
  id?: number | string;
  dispatch_id: number;
  name: string;
  attachment?: string | null;
  enabled: boolean;
  created_at: string;
  updated_at?: string;
  created_by?: number;
  updater?: User;
}

export interface DispatchComment {
  id?: number | string;
  dispatch_id: number;
  user_id?: number;
  comment: string;
  created_at: string;
  user?: User;
  creator?: User;
}

export interface DispatchReview {
  id?: number | string;
  dispatch_id: number;
  customer_id?: number;
  review: string;
  rating?: number | null;
  created_at: string;
  customer?: Customer;
  creator?: User;
}

export interface StatusLog {
  id: number;
  portal: 'admin' | 'customer';
  module: string;
  reference_id: number;
  from_status: string | null;
  to_status: string;
  updated_by?: number | null;
  updated_at: string;
  created_at: string;
  updater?: User;
}

export interface Dispatch {
  id: number;
  type?: 'personal' | 'business';
  reference_id?: number;
  reference_number?: string;
  customer_id?: number;
  business_id?: number;
  task_type_id?: number;
  year?: string;
  task_id?: number;
  task_template_id?: number;
  create_date?: string;
  collect_date?: string;
  collect_due?: string;
  return_date?: string;
  return_due?: string;
  location?: string;
  agent_id?: number;
  status?: string;
  enabled: boolean;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  customer?: Customer;
  business?: Business;
  taskType?: TaskType;
  task?: Task;
  taskTemplate?: TaskTemplate;
  task_template?: TaskTemplate;
  agent?: User;
  checkins?: DispatchCheckin[];
  checkouts?: DispatchCheckout[];
  collects?: DispatchCollect[];
  returns?: DispatchReturn[];
  comments?: DispatchComment[];
  reviews?: DispatchReview[];
  acknowledge_logs?: StatusLog[];
  status_logs?: StatusLog[];
}

export interface CreateDispatchData {
  type?: 'personal' | 'business';
  reference_id?: number;
  customer_id?: number;
  business_id?: number;
  task_type_id?: number;
  year?: number;
  task_id?: number;
  task_template_id?: number;
  create_date?: string;
  collect_date?: string;
  collect_due?: string;
  return_date?: string;
  return_due?: string;
  location?: string;
  agent_id?: number;
  status?: string;
  enabled?: boolean;
  checkins?: Array<{
    id?: number | string;
    name: string;
    completed?: boolean;
  }>;
  checkouts?: Array<{
    id?: number | string;
    name: string;
    completed?: boolean;
  }>;
  collects?: Array<{
    id?: number | string;
    name: string;
    enabled?: boolean;
    attachment?: string | null;
  }>;
  returns?: Array<{
    id?: number | string;
    name: string;
    enabled?: boolean;
    attachment?: string | null;
  }>;
  comments?: Array<{
    id?: number | string;
    comment: string;
  }>;
  reviews?: Array<{
    id?: number | string;
    customer_id?: number;
    review: string;
  }>;
}

export const dispatchService = {
  async getAll(params?: { page?: number; per_page?: number }) {
    const response = await api.get('/dispatches', { params });
    return response.data;
  },

  async getById(id: number): Promise<Dispatch> {
    const response = await api.get(`/dispatches/${id}`);
    return unwrap<Dispatch>(response);
  },

  async create(data: CreateDispatchData | FormData): Promise<Dispatch> {
    const response = await api.post('/dispatches', data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
    return unwrap<Dispatch>(response);
  },

  async update(id: number, data: Partial<CreateDispatchData> | FormData): Promise<Dispatch> {
    if (data instanceof FormData) {
      data.append('_method', 'PUT');
      const response = await api.post(`/dispatches/${id}`, data);
      return unwrap<Dispatch>(response);
    }
    const response = await api.put(`/dispatches/${id}`, data);
    return unwrap<Dispatch>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/dispatches/${id}`);
  },
  async import(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/dispatches/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  async downloadImportTemplate() {
    const response = await api.get('/dispatches/import/template', {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  async export(payload: { ids?: number[]; select_all?: boolean; excluded_ids?: number[]; filters?: Record<string, any> }) {
    const response = await api.post(
      '/dispatches/export',
      payload,
      { responseType: 'blob' }
    );
    return response.data as Blob;
  },
  async batchDelete(payload: { ids?: number[]; select_all?: boolean; excluded_ids?: number[]; filters?: Record<string, any> }) {
    const response = await api.post('/dispatches/batch-delete', payload);
    return response.data;
  },
};

export const dispatchCommentService = {
  async delete(id: number): Promise<void> {
    await api.delete(`/dispatch-comments/${id}`);
  },
};
